import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getToken } from '../../api/client';
import {
  workspacesApi,
  setWorkspaceId,
  type WorkspaceInfo,
  type WorkspaceImportResult,
  type WorkspaceImportPreview,
} from '../../api/workspaces';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../utils/formatDate';

export default function WorkspacesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const { data: workspaces, isLoading } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
  });

  const [deletingWorkspaceName, setDeletingWorkspaceName] = useState('');

  const deleteMutation = useMutation({
    mutationFn: (id: string) => workspacesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      if (deletingWorkspaceName) {
        addToast(`Workspace "${deletingWorkspaceName}" deleted`, 'success');
        setDeletingWorkspaceName('');
      }
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => workspacesApi.activate(id),
    onSuccess: (ws: WorkspaceInfo) => {
      setWorkspaceId(ws.id);
      queryClient.invalidateQueries();
    },
  });

  const toggleAiMutation = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      workspacesApi.update(id, { aiEgressDisabled: disabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const ws = await workspacesApi.create({ name: newName.trim() });
      await activateMutation.mutateAsync(ws.id);
      setNewName('');
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      navigate('/');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (ws: WorkspaceInfo) => {
    if (!confirm(`Delete workspace "${ws.name}"? This cannot be undone.`)) return;
    setDeletingWorkspaceName(ws.name);
    deleteMutation.mutate(ws.id);
  };

  const handleExport = (ws: WorkspaceInfo) => {
    // Download via authenticated fetch (cookie + Bearer when in memory)
    const token = getToken();
    const url = `${import.meta.env.VITE_API_URL ?? '/api'}/workspaces/${ws.id}/export`;
    fetch(url, {
      credentials: 'include',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Export failed');
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${ws.name}.db`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((err) => alert(err.message));
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-xl font-bold text-gray-900">Workspaces</h1>

      {/* Create workspace */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900 mb-3">New Workspace</h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md"
            placeholder="Workspace name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <button
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>

      {/* Import */}
      <ImportSection
        onImported={(wsId: string) => {
          activateMutation.mutate(wsId);
          queryClient.invalidateQueries({ queryKey: ['workspaces'] });
          navigate('/');
        }}
      />

      {/* Workspace list */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">All Workspaces</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th className="text-left">Name</th>
                <th className="text-left">Company</th>
                <th className="text-left">Created</th>
                <th className="text-left">Last Opened</th>
                <th className="text-left">Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {workspaces?.map((ws: WorkspaceInfo) => (
                <tr key={ws.id} className={`border-b border-gray-50 ${ws.isActive ? 'bg-primary-50/50' : ''}`}>
                  <td className="font-medium">{ws.name}</td>
                  <td className="text-gray-500">{ws.companyName ?? '-'}</td>
                  <td className="text-gray-500">{formatDate(ws.createdAt)}</td>
                  <td className="text-gray-500">{formatDate(ws.lastOpenedAt)}</td>
                  <td>
                    {ws.isActive ? (
                      <span className="text-xs font-medium text-green-600">Active</span>
                    ) : (
                      <span className="text-xs text-gray-400">Inactive</span>
                    )}
                  </td>
                  <td className="text-right space-x-2">
                    {!ws.isActive && (
                      <button
                        className="text-xs text-primary-600 hover:underline"
                        onClick={() => activateMutation.mutate(ws.id)}
                        disabled={activateMutation.isPending}
                      >
                        Activate
                      </button>
                    )}
                    <button
                      className={`text-xs ${ws.aiEgressDisabled ? 'text-red-500 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                      onClick={() => toggleAiMutation.mutate({ id: ws.id, disabled: !ws.aiEgressDisabled })}
                      disabled={toggleAiMutation.isPending}
                      title={ws.aiEgressDisabled ? 'AI disabled — click to enable' : 'AI enabled — click to disable'}
                    >
                      {ws.aiEgressDisabled ? '🔒 AI Off' : '🤖 AI On'}
                    </button>
                    <button
                      className="text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => handleExport(ws)}
                    >
                      Export
                    </button>
                    {!ws.isActive && (
                      <button
                        className="text-xs text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(ws)}
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(!workspaces || workspaces.length === 0) && (
                <tr>
                  <td colSpan={6} className="text-center text-gray-400 py-4">
                    No workspaces. Create one or import a legacy database.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

type ImportStep = 'select' | 'preview' | 'importing' | 'success' | 'error';

function ImportSection({ onImported }: { onImported: (wsId: string) => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<WorkspaceImportPreview | null>(null);
  const [importResult, setImportResult] = useState<WorkspaceImportResult | null>(null);
  const [error, setError] = useState('');

  const handleFileSelect = () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    setSelectedFile(file);
  };

  const handlePreview = async () => {
    if (!selectedFile) return;
    setStep('importing');
    setError('');
    try {
      const result = await workspacesApi.previewImport(selectedFile);
      setPreview(result);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
      setStep('error');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setStep('importing');
    setError('');
    try {
      const result = await workspacesApi.importLegacy(selectedFile);
      setImportResult(result);
      setStep('success');
      if (result.workspaceId) {
        onImported(result.workspaceId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setStep('error');
    }
  };

  const handleReset = () => {
    setStep('select');
    setSelectedFile(null);
    setPreview(null);
    setImportResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-200">
        <h2 className="font-semibold text-gray-900">Import Legacy Database</h2>
      </div>
      <div className="p-4">
        {step === 'select' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Upload an Apskaita5 .db file to create a new workspace and import data.
            </p>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".db,.sqlite,.sqlite3"
                className="text-sm"
                onChange={handleFileSelect}
              />
              {selectedFile && (
                <>
                  <span className="text-sm text-gray-500">
                    {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                  </span>
                  <button
                    className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
                    onClick={handlePreview}
                  >
                    Preview Import
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-3">
            {preview.alreadyImported && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  This database was already imported as workspace &quot;{preview.existingWorkspaceName}&quot;.
                  Re-importing will create a separate workspace with the same data.
                </p>
              </div>
            )}
            <table className="dense w-full">
              <thead>
                <tr>
                  <th className="text-left">Entity</th>
                  <th className="text-right">Records</th>
                </tr>
              </thead>
              <tbody>
                {preview.tables.map((t) => (
                  <tr key={t.tableName}>
                    <td>{t.displayName}</td>
                    <td className="num">{t.rowCount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center gap-2">
              <button
                className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
                onClick={handleImport}
              >
                Import into New Workspace
              </button>
              <button
                className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={handleReset}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {step === 'importing' && <LoadingSpinner />}

        {step === 'success' && importResult && (
          <div className="space-y-3">
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm font-medium text-green-800">
                Import successful! {importResult.totalRecordsImported} records imported into workspace &quot;{importResult.workspaceName}&quot;.
              </p>
            </div>
            {Object.keys(importResult.recordsPerTable).length > 0 && (
              <table className="dense w-full">
                <thead>
                  <tr>
                    <th className="text-left">Table</th>
                    <th className="text-right">Records</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(importResult.recordsPerTable).map(([table, count]) => (
                    <tr key={table}>
                      <td>{table}</td>
                      <td className="num">{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button
              className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={handleReset}
            >
              Done
            </button>
          </div>
        )}

        {step === 'error' && (
          <div className="space-y-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button
              className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              onClick={handleReset}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
